import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Microscope, TrendingUp, Users, ArrowRight, Activity, CheckCircle2, ExternalLink } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { EnhancedScrollReveal } from '../common/EnhancedScrollReveal';
import { BarChart } from '../ui/DataVisualization';
const studyData = [{
  label: 'Digestibilidad',
  value: 92,
  color: 'bg-emerald-500',
  suffix: '%'
}, {
  label: 'Energía Vital',
  value: 85,
  color: 'bg-amber-500',
  suffix: '%'
}, {
  label: 'Salud Dérmica',
  value: 88,
  color: 'bg-blue-500',
  suffix: '%'
}, {
  label: 'Salud Dental',
  value: 75,
  color: 'bg-purple-500',
  suffix: '%'
}];
export function ScientificEvidence() {
  return <section className="py-24 lg:py-32 bg-white overflow-hidden relative" data-id="element-3296">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-40" data-id="element-3297" />

      <div className="container-custom max-w-7xl mx-auto relative z-10" data-id="element-3298">
        <div className="flex flex-col lg:flex-row justify-between items-end mb-20 gap-8" data-id="element-3299">
          <div className="max-w-2xl" data-id="element-3300">
            <EnhancedScrollReveal data-id="element-3301">
              <Badge variant="secondary" className="mb-6 bg-blue-50 text-blue-700 border-blue-100" data-id="element-3302">
                <Microscope className="w-3 h-3 mr-1" data-id="element-3303" /> Evidencia Clínica
              </Badge>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-charcoal-900 mb-6 leading-tight" data-id="element-3304">
                Resultados probados, <br data-id="element-3305" />
                <span className="text-blue-600" data-id="element-3306">no promesas</span>
              </h2>
              <p className="text-lg text-charcoal-600 leading-relaxed font-light" data-id="element-3307">
                Nuestras fórmulas no se basan en tendencias, sino en datos.
                Realizamos estudios clínicos independientes para validar cada
                beneficio.
              </p>
            </EnhancedScrollReveal>
          </div>

          <EnhancedScrollReveal delay={0.2} data-id="element-3308">
            <Button variant="outline" className="rounded-full border-charcoal-200" data-id="element-3309">
              Ver Biblioteca de Estudios <ArrowRight className="ml-2 w-4 h-4" data-id="element-3310" />
            </Button>
          </EnhancedScrollReveal>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-start" data-id="element-3311">
          {/* Left: Clinical Report Card */}
          <div className="lg:col-span-7" data-id="element-3312">
            <EnhancedScrollReveal direction="right" className="h-full" data-id="element-3313">
              <div className="bg-white rounded-[2.5rem] p-8 md:p-12 border border-gray-100 shadow-2xl relative overflow-hidden h-full" data-id="element-3314">
                <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-6 py-3 rounded-bl-3xl" data-id="element-3315">
                  Estudio Clínico #2023-A
                </div>

                <div className="mb-12" data-id="element-3316">
                  <h3 className="text-2xl font-bold text-charcoal-900 mb-2 font-serif" data-id="element-3317">
                    Eficacia Nutricional Comparativa
                  </h3>
                  <p className="text-charcoal-500 text-sm" data-id="element-3318">
                    Estudio de 90 días en cohorte de 500 perros. Comparativa vs.
                    dieta seca premium.
                  </p>
                </div>

                <div className="h-[350px] w-full mb-12" data-id="element-3319">
                  <BarChart data={studyData} height={350} data-id="element-3320" />
                </div>

                <div className="grid grid-cols-2 gap-6 pt-8 border-t border-gray-100" data-id="element-3321">
                  <div className="flex items-center gap-3" data-id="element-3322">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-3323">
                      <Users className="w-5 h-5" data-id="element-3324" />
                    </div>
                    <div data-id="element-3325">
                      <div className="text-xs text-gray-400 uppercase font-bold" data-id="element-3326">
                        Muestra
                      </div>
                      <div className="font-bold text-charcoal-900" data-id="element-3327">
                        n = 500 mascotas
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-3328">
                    <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600" data-id="element-3329">
                      <CheckCircle2 className="w-5 h-5" data-id="element-3330" />
                    </div>
                    <div data-id="element-3331">
                      <div className="text-xs text-gray-400 uppercase font-bold" data-id="element-3332">
                        Validación
                      </div>
                      <div className="font-bold text-charcoal-900" data-id="element-3333">
                        Revisado por pares
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </EnhancedScrollReveal>
          </div>

          {/* Right: Key Findings */}
          <div className="lg:col-span-5 space-y-6" data-id="element-3334">
            <EnhancedScrollReveal delay={0.2} data-id="element-3335">
              <div className="group p-8 rounded-[2rem] bg-gray-50 border border-gray-100 hover:bg-white hover:shadow-xl transition-all duration-300 cursor-pointer" data-id="element-3336">
                <div className="flex items-start justify-between mb-4" data-id="element-3337">
                  <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform" data-id="element-3338">
                    <TrendingUp className="w-6 h-6" data-id="element-3339" />
                  </div>
                  <ExternalLink className="w-5 h-5 text-gray-300 group-hover:text-blue-500 transition-colors" data-id="element-3340" />
                </div>
                <h4 className="text-xl font-bold text-charcoal-900 mb-3" data-id="element-3341">
                  Longevidad Aumentada
                </h4>
                <p className="text-charcoal-600 text-sm leading-relaxed mb-4" data-id="element-3342">
                  Estudios longitudinales sugieren que una dieta fresca puede
                  extender la esperanza de vida hasta en 3 años al reducir el
                  estrés oxidativo celular.
                </p>
                <div className="text-xs font-bold text-blue-600 uppercase tracking-wide" data-id="element-3343">
                  Ref: Lippert & Sapy Study
                </div>
              </div>
            </EnhancedScrollReveal>

            <EnhancedScrollReveal delay={0.3} data-id="element-3344">
              <div className="group p-8 rounded-[2rem] bg-gray-50 border border-gray-100 hover:bg-white hover:shadow-xl transition-all duration-300 cursor-pointer" data-id="element-3345">
                <div className="flex items-start justify-between mb-4" data-id="element-3346">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform" data-id="element-3347">
                    <Activity className="w-6 h-6" data-id="element-3348" />
                  </div>
                  <ExternalLink className="w-5 h-5 text-gray-300 group-hover:text-emerald-500 transition-colors" data-id="element-3349" />
                </div>
                <h4 className="text-xl font-bold text-charcoal-900 mb-3" data-id="element-3350">
                  Biodisponibilidad Superior
                </h4>
                <p className="text-charcoal-600 text-sm leading-relaxed mb-4" data-id="element-3351">
                  La cocción suave a 80°C permite una absorción de nutrientes
                  del 95%, comparado con el 60-70% de los piensos extruidos a
                  alta temperatura.
                </p>
                <div className="text-xs font-bold text-emerald-600 uppercase tracking-wide" data-id="element-3352">
                  Ref: Journal of Animal Science, 2022
                </div>
              </div>
            </EnhancedScrollReveal>

            <EnhancedScrollReveal delay={0.4} data-id="element-3353">
              <div className="bg-charcoal-900 rounded-3xl p-8 text-white text-center" data-id="element-3354">
                <h4 className="font-serif font-bold text-xl mb-2" data-id="element-3355">
                  ¿Eres veterinario?
                </h4>
                <p className="text-gray-400 text-sm mb-6" data-id="element-3356">
                  Accede a nuestros white papers y fichas técnicas completas.
                </p>
                <Button variant="white" size="sm" className="rounded-full w-full" data-id="element-3357">
                  Acceso Profesional
                </Button>
              </div>
            </EnhancedScrollReveal>
          </div>
        </div>
      </div>
    </section>;
}